<template>
  <div class="auth-login__field">
    <div v-if="forgotLabel" class="auth-login__label-row">
      <label :for="id" class="auth-login__label">{{ label }}</label>
      <button type="button" class="auth-login__link" @click="emit('forgot')">
        {{ forgotLabel }}
      </button>
    </div>
    <label v-else :for="id" class="auth-login__label">{{ label }}</label>

    <div class="auth-login__password-wrap">
      <input
        :id="id"
        :data-testid="testId || undefined"
        :name="name || undefined"
        ref="inputRef"
        class="auth-login__input auth-login__input--password"
        :class="{ 'auth-login__input--error': error }"
        :type="visible ? 'text' : 'password'"
        :value="modelValue"
        :placeholder="placeholder"
        :autocomplete="autocomplete"
        :required="required"
        :aria-invalid="error ? 'true' : undefined"
        :aria-describedby="describedBy || undefined"
        @focus="syncFromDomValue"
        @paste="onPaste"
        @input="onInput"
        @change="onChange"
      />
      <button
        type="button"
        class="auth-login__password-toggle"
        :data-testid="toggleTestId || undefined"
        :aria-label="visible ? 'Hide password' : 'Show password'"
        :aria-pressed="visible ? 'true' : 'false'"
        @click="visible = !visible"
      >
        <img v-if="classic" class="auth-login__classic-password-eye" src="/images/icons/eye.png" alt="">
        <AuthIcon v-else :name="visible ? 'eye-off' : 'eye'" />
      </button>
    </div>

    <span v-if="hint && !error" :id="`${id}-hint`" class="auth-login__hint">{{ hint }}</span>
    <span v-if="error" :id="`${id}-error`" class="auth-login__error" role="alert">{{ error }}</span>
  </div>
</template>

<script setup>
import { computed, nextTick, onMounted, ref } from 'vue'
import AuthIcon from './AuthIcon.vue'

const props = defineProps({
  id:           { type: String, required: true },
  name:         { type: String, default: '' },
  modelValue:   { type: String, default: '' },
  label:        { type: String, required: true },
  placeholder:  { type: String, default: '' },
  autocomplete: { type: String, default: 'new-password' },
  hint:         { type: String, default: '' },
  error:        { type: String, default: '' },
  forgotLabel:  { type: String, default: '' },
  required:     { type: Boolean, default: false },
  testId:       { type: String, default: '' },
  toggleTestId: { type: String, default: '' },
  classic:      { type: Boolean, default: false },
})

const emit = defineEmits(['update:modelValue', 'forgot'])

const inputRef = ref(null)
const visible = ref(false)

const describedBy = computed(() => {
  if (props.error) return `${props.id}-error`
  if (props.hint) return `${props.id}-hint`
  return ''
})

function sanitizePassword(value) {
  return String(value ?? '').replace(/\s+/g, '')
}

function emitSanitizedValue(value) {
  const sanitized = sanitizePassword(value)

  if (inputRef.value && inputRef.value.value !== sanitized) {
    inputRef.value.value = sanitized
  }

  if (sanitized !== props.modelValue) {
    emit('update:modelValue', sanitized)
  }
}

function syncFromDomValue() {
  emitSanitizedValue(inputRef.value?.value ?? '')
}

function onInput(event) {
  emitSanitizedValue(event.target.value)
}

function onChange(event) {
  emitSanitizedValue(event.target.value)
}

function onPaste() {
  nextTick(syncFromDomValue)
}

onMounted(() => {
  nextTick(syncFromDomValue)
})

defineExpose({
  getInputElement: () => inputRef.value,
})
</script>
